import { useDraggable } from '@dnd-kit/core'
import type { BloggerListItem, BloggerStage } from '@ayub/shared'
import { formatNumber } from '@ayub/shared'
import { cn } from '@/lib/utils'
import { BLOGGER_STAGE_META } from './stageMeta'

/** dnd-kit draggable id'sini bloger id'siga aylantirish (va orqaga) uchun bitta joyda. */
export function bloggerDragId(id: number): string {
  return `blogger-${id}`
}

export interface BloggerDragData {
  blogger: BloggerListItem
  stage: BloggerStage
}

interface CardContentProps {
  blogger: BloggerListItem
  /** Optimistik surilgan bo'lsa, kartochka rangi/yorlig'i YANGI ustunga mos ko'rsatiladi. */
  displayStage: BloggerStage
  dragging?: boolean
}

/**
 * Kartochka ko'rinishi — `docs/04-ui-ux.md` "Kanban kartochkasi" uslubiga mos: chap chetida
 * 3px rangli chiziq, ~88px balandlik, sarlavha 14/600, qolgani hint rangda.
 *
 * Alohida eksport qilingan: xuddi shu JSX ham haqiqiy sudraladigan kartochkada (`BloggerKanbanCard`),
 * ham `DragOverlay` ichida (sudralayotgan nusxa) ishlatiladi.
 */
export function BloggerKanbanCardContent({ blogger, displayStage, dragging }: CardContentProps) {
  const meta = BLOGGER_STAGE_META[displayStage]
  return (
    <div
      className={cn(
        'flex min-h-[88px] flex-col justify-center gap-1 rounded-card border-l-[3px] border-app-border bg-app-card p-3 transition-shadow duration-150 ease-out',
        meta.borderClass,
        dragging
          ? 'rotate-3 shadow-[0_8px_20px_rgba(0,0,0,.18)]'
          : 'shadow-[0_1px_3px_rgba(0,0,0,.06)] dark:shadow-none',
      )}
    >
      <span className="text-sm font-semibold text-app-text">@{blogger.instagramUsername}</span>
      <span className="text-xs text-app-hint">{blogger.fullName}</span>
      <span className="tabular text-xs text-app-hint">
        {formatNumber(blogger.followers)} obunachi · {formatNumber(blogger.videoCount)} video
      </span>
    </div>
  )
}

interface BloggerKanbanCardProps {
  blogger: BloggerListItem
  stage: BloggerStage
  onCardClick: (id: number) => void
}

/** Sudraladigan (draggable) kartochka — haqiqiy ustun ichida ko'rsatiladi. */
export function BloggerKanbanCard({ blogger, stage, onCardClick }: BloggerKanbanCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: bloggerDragId(blogger.id),
    data: { blogger, stage } satisfies BloggerDragData,
  })

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={() => onCardClick(blogger.id)}
      onKeyDown={(event) => {
        // dnd-kit `listeners.onKeyDown` — Space/Escape/strelka tugmalarini sudrash uchun
        // band qiladi (`KeyboardSensor`). Enter esa BO'SH qoladi — shu yerda uni "ochish"
        // amaliga beramiz, shunda kartochka ham to'liq klaviatura bilan sudraladi, ham
        // to'liq klaviatura bilan ochiladi (`docs/04-ui-ux.md`: "Klaviatura bilan to'liq
        // boshqarish").
        listeners?.onKeyDown?.(event)
        if (event.key === 'Enter') {
          onCardClick(blogger.id)
        }
      }}
      className={cn(
        // `touch-manipulation` (`touch-none` EMAS) — dnd-kit hujjati aynan shu TouchSensor
        // (delay:200, tolerance:8) kombinatsiyasi uchun shuni tavsiya qiladi: `touch-none`
        // brauzerga touchstart'dagi ZAHOTIYOQ "bu elementda skroll yo'q" deb qaror
        // qildiradi va bu qaror keyinchalik delay/tolerance natijasiga qarab bekor
        // qilinmaydi — natijada zich ustunda vertikal skroll boshlash amalda imkonsiz
        // bo'lib qoladi. `touch-manipulation` esa faqat double-tap-zoom'ni o'chiradi,
        // vertikal panga (skroll) yo'l qo'yadi, sensor esa 200ms/8px ichida o'zi hal qiladi.
        'cursor-grab touch-manipulation select-none outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand active:cursor-grabbing',
        isDragging && 'opacity-30',
      )}
    >
      <BloggerKanbanCardContent blogger={blogger} displayStage={stage} />
    </div>
  )
}
